import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Play, Clock, User, Search, Filter } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TelecallerService } from '../../services/telecallerService';
import { maskPhoneLast4 } from '../../utils/phoneMask';

const CallRecordings = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const { data: recordingsData, isLoading, refetch } = useQuery({
    queryKey: ['telecallerCallRecordings', user?.id],
    queryFn: async () => {
      // Fetch call logs with recordings
      const result = await TelecallerService.getLeadsHistory(user?.id);
      if (result.success && result.data) {
        // Filter recordings that have recording_url
        return result.data.filter(
          (log) => log.recording_url && log.recording_url.trim() !== ''
        );
      }
      return [];
    },
    enabled: !!user?.id,
  });

  const recordings = recordingsData || [];

  const filteredRecordings = recordings.filter((recording) => {
    const matchesSearch = !searchQuery || 
      recording.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recording.contact_number?.includes(searchQuery);
    const matchesStatus = filterStatus === 'all' || recording.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const playRecording = (recording) => {
    if (!recording.recording_url) {
      alert('No recording URL available');
      return;
    }
    window.open(recording.recording_url, '_blank');
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'in-progress') return 'bg-yellow-100 text-yellow-700';
    if (s === 'missed') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/telecaller/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Call Recordings</h1>
          <p className="text-gray-600 mt-1">Access your call recordings</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="missed">Missed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recordings List */}
      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading recordings...</p>
        </div>
      ) : filteredRecordings.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <Phone size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No call recordings found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRecordings.map((recording) => (
            <div
              key={recording.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {recording.customer_name || 'Unknown Customer'}
                      </h3>
                      <p className="text-sm text-gray-600">{maskPhoneLast4(recording.contact_number) || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Date & Time</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(recording.call_time || recording.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDuration(recording.duration_seconds)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getStatusColor(recording.status)}`}>
                        {recording.status || 'N/A'}
                      </span>
                    </div>
                    {recording.order_id && (
                      <div>
                        <p className="text-xs text-gray-500">Order ID</p>
                        <p className="text-sm font-medium text-gray-900">
                          #{recording.order_id?.slice(0, 8)}
                        </p>
                      </div>
                    )}
                  </div>

                  {recording.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{recording.notes}</p>
                    </div>
                  )}
                </div>

                <div className="ml-4">
                  <button
                    onClick={() => playRecording(recording)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    <Play size={18} />
                    <span>Play</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CallRecordings;

